<template>
  <div class="model-select-wrapper h-100" v-click-outside="hideDropdown">
    <div
      class="form-control-wrapper"
      @click.prevent="toggleDropdown"
      :class="formClass"
    >
      <span class="dropdown-icon" v-if="!disabled">
        <i class="fas fa-caret-down text-secondary"></i>
      </span>
      <input
        type="text"
        :id="_uid"
        ref="display"
        class="form-control"
        :class="classes"
        :required="required"
        :placeholder="disabled || !isSearchable ? '-' : placeholder"
        :readonly="true"
        :value="customDisplay || display"
      />
    </div>
    <div class="model-select-dropdown card" v-if="dropdownVisible">
      <div
        class="card-header d-flex justify-content-between"
        v-if="showTitle || canCreate"
      >
        <span class="text-secondary" v-if="showTitle">{{ title }}</span>
        <span>
          <a
            href="#new"
            class="text-warning mr-3"
            @click.prevent="onNew"
            v-if="canCreate"
          >
            <i class="fas fa-plus"></i>
            <small>NUOVO</small>
          </a>
          <a href="#hide" class="text-secondary" @click.prevent="hideDropdown">
            <i class="fas fa-times"></i>
          </a>
        </span>
      </div>
      <div class>
        <div class="input-wrapper" v-if="canSearch">
          <a
            href="#clear"
            class="icon-right text-light"
            v-if="isSearchable && searching && !loading"
            @click.prevent="resetSearch"
          >
            <i class="fas fa-times"></i>
          </a>
          <span class="icon-right" v-if="loading">
            <i class="fas fa-circle-notch fa-spin"></i>
          </span>
          <input
            type="text"
            ref="input"
            class="form-control"
            required
            v-model="term"
            @input="onKeyUp"
            :placeholder="formattedPlaceholder"
            :disabled="loading"
          />
        </div>
        <div
          class="options-wrapper"
          :class="{ 'no-search': canSearch == false }"
          v-if="searching"
        >
          <ul class="list-group list-group-flush" v-if="options.length">
            <li
              class="list-group-item"
              v-for="option in options"
              :key="option.id"
              @click.prevent="onSelect(option)"
            >
              <slot name="option" :value="option">{{ option.name }}</slot>
            </li>
          </ul>
          <div class="card-empty" v-else>Nessun risultato</div>
        </div>
      </div>
      <div class="card-footer" v-if="canSearch && searching && options.length">
        <small
          >{{ options.length }} risultat{{
            options.length == 1 ? "o" : "i"
          }}</small
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ClickOutside from "directives/ClickOutside";
import { Component, Vue, Prop, Watch } from "vue-property-decorator";

@Component({ directives: { ClickOutside } })
export default class ModelSelect extends Vue {
  @Prop() value!: any;
  @Prop() id: string | number;
  @Prop() classes: string;
  @Prop({ default: false }) required: boolean;
  @Prop({ default: "Cerca e seleziona" }) title: string;
  @Prop() formClass: string;
  @Prop({ default: true }) showTitle: boolean;
  @Prop({ default: `(min. %d caratteri)` }) placeholder: string;
  @Prop({ default: 3 }) minSearchLength: number;
  @Prop({ default: false }) actsLikeFilter: boolean;
  @Prop({ default: 350 }) inputTimeout: number;
  @Prop({ default: "name" }) displayProperty: string;
  @Prop({ default: true }) canCreate: boolean;
  @Prop({ default: () => [] }) defaultOptions: any[] | null;
  @Prop({ default: true }) canSearch: boolean;
  @Prop({ default: false }) disabled: boolean;
  @Prop() customDisplay: string;

  private dropdownVisible = false;
  private term: string | null = null;
  private loading = false;
  private searching = false;
  private timeout = null;
  private options = [];
  private selected = this.value;
  private stateChanger = {};
  private searchActive = false;

  @Watch("value")
  onValueChanged(value) {
    this.selected = value;
  }

  @Watch("id")
  onIdChanged(newValue, oldValue) {
    if (!newValue && oldValue) this.selected = null;
  }

  @Watch("dropdownVisible")
  onDropdownVisibleChanged(value) {
    if (value) {
      this.$nextTick(() => {
        if (this.$refs.input) {
          (this.$refs.input as HTMLInputElement).focus();
        }
      });
    } else {
      this.resetSearch();
    }
  }

  @Watch("defaultOptions", { immediate: true, deep: true })
  onDefaultOptionsChanged(newValue, oldValue) {
    if (!this.value && this.id) {
      if (this.defaultOptions.length) {
        this.options = this.defaultOptions;
        this.selected = this.options.find((o) => o.id == this.id);
      }
    }
  }

  get display() {
    return this.selected ? this.selected[this.displayProperty] : null;
  }

  get formattedPlaceholder() {
    return this.placeholder.replace("%d", this.minSearchLength + "");
  }

  get params() {
    return {
      term: this.term,
    };
  }

  get isSearchable() {
    if (!this.term) {
      return false;
    }

    return this.term.length >= this.minSearchLength || !this.term.length;
  }

  created() {
    this.stateChanger = {
      searching: () => {
        this.loading = true;
      },
      searched: (options) => {
        this.options = options;
        this.searching = true;
        this.loading = false;
      },
      error: () => {
        // TODO: error handling implementation
      },
    };

    if (this.defaultOptions.length) {
      this.options = this.defaultOptions;
      this.searching = true;
    }
  }

  toggleDropdown() {
    if (!this.disabled) this.dropdownVisible = !this.dropdownVisible;
  }

  hideDropdown() {
    this.dropdownVisible = false;
  }

  resetSearch() {
    if (this.defaultOptions.length) {
      this.options = this.defaultOptions;
      this.searching = true;
    } else {
      this.options = [];
      this.searching = false;
    }

    this.loading = false;
    this.term = null;
    this.$emit("search", this.term, this.stateChanger);
  }

  onKeyUp(event) {
    if (this.isSearchable) {
      this.searchActive = true;
      this.emitSearch(this.term);
    } else if (this.searchActive) {
      this.searchActive = false;
      this.emitSearch("");
    }
  }

  emitSearch(term: string) {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => {
      this.$emit("search", term, this.stateChanger);
    }, this.inputTimeout);
  }

  onSelect(option) {
    this.selected = option;
    this.hideDropdown();

    this.$emit("input", option);
    this.$emit("selected", option);
  }

  onNew() {
    this.$emit("new");
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/shared/variables";

.model-select-wrapper {
  position: relative;
}

.form-control {
  height: inherit;
}

.form-control[readonly] {
  background-color: transparent;
}

.form-control-wrapper {
  position: relative;
  cursor: pointer;

  .dropdown-icon {
    position: absolute;
    right: 0.7rem;
    top: 0.6rem;
    font-size: 0.65rem;
    color: $primary;
  }

  input {
    cursor: pointer !important;
    padding-right: 0.5rem;
    user-select: none;

    &::selection {
      background: transparent !important;
    }
  }
}

.model-select-dropdown {
  position: absolute;
  z-index: 2;
  width: 100%;
  margin-top: 0;
  box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.1);
  border-top-left-radius: 0;
  border-top-right-radius: 0;

  .input-wrapper {
    position: relative;

    input {
      // border: 1px solid $border-color;
      // padding: $input-padding-y $input-padding-y * 2;
      // @include border-radius(0.25rem);

      &:disabled {
        // background: $gray-100;
        // color: $gray-600;
      }
    }

    .icon-right {
      position: absolute;
      right: 0.5rem;
      top: 50%;
      transform: translateY(-50%);
      color: $secondary;
    }
  }

  .options-wrapper {
    max-height: 250px;
    // border-top: 1px solid $border-color;
    margin-top: 1.25rem;
    overflow-y: auto;

    &.no-search {
      border-top: 0;
      margin-top: 0;
    }

    .card-empty {
      height: 100%;
      min-height: 0;
    }

    .list-group-item {
      cursor: pointer;

      &:hover {
        color: $primary;
      }
    }
  }
}
</style>
